import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useT } from '../lib/i18n.js';
import { errorMessage } from '../lib/properties.js';
import {
  dismissMatch,
  listDemandMatches,
  listPropertyMatches,
  previewOffer,
  sendOffer,
  type DemandMatch,
  type OfferPreview,
} from '../lib/matches-api.js';
import { V4Group, V4Pill, V4Row } from './ux-v4.js';

/**
 * Shody poptávka × nemovitost (krok CRM4, bod 2).
 *
 *  - na kartě NEMOVITOSTI (`propertyId`): poptávky, které jí odpovídají,
 *    s tlačítkem „Poslat nabídku" → náhled e-mailu v brandingu kanceláře →
 *    teprve „Odeslat" (nic neodejde bez potvrzení makléře),
 *  - na kartě POPTÁVKY (`demandId`): vhodné nemovitosti s odkazem na kartu.
 *
 * Skóre i věta „Sedí lokalita a cena, dispozice o jednu menší" jsou ze
 * serveru (`@reos/crm` matching-rules) — UI je nepočítá.
 */
export function MatchesPanel(
  props: { readonly propertyId: string; readonly demandId?: undefined } | { readonly demandId: string; readonly propertyId?: undefined },
): React.JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const byProperty = props.propertyId !== undefined;
  const scopeId = props.propertyId ?? props.demandId ?? '';
  const queryKey = ['matches', byProperty ? 'property' : 'demand', scopeId];
  const matches = useQuery({
    queryKey,
    queryFn: () => (byProperty ? listPropertyMatches(scopeId) : listDemandMatches(scopeId)),
  });
  const [preview, setPreview] = React.useState<{ readonly match: DemandMatch; readonly mail: OfferPreview } | null>(null);
  const [note, setNote] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['matches'] }),
      queryClient.invalidateQueries({ queryKey: ['new-matches'] }),
    ]);
  }

  const items = matches.data ?? [];
  const testPrefix = byProperty ? 'property-matches' : 'demand-matches';

  return (
    <div className="rh-crm4-panel" data-testid={testPrefix}>
      {matches.isLoading ? (
        <p role="status" className="rh-a2-status">
          {t('webAdmin.matches.loading')}
        </p>
      ) : null}
      {matches.error !== null ? (
        <p role="alert" className="rh-a2-error">
          {errorMessage(matches.error)}
        </p>
      ) : null}
      {error !== null ? (
        <p role="alert" className="rh-a2-error" data-testid={`${testPrefix}-error`}>
          {error}
        </p>
      ) : null}
      {note !== null ? (
        <p role="status" className="rh-a2-status" data-testid={`${testPrefix}-note`}>
          {note}
        </p>
      ) : null}
      <V4Group testId={`${testPrefix}-list`} label={t(byProperty ? 'webAdmin.matches.propertyHeading' : 'webAdmin.matches.demandHeading')}>
        {matches.data !== undefined && items.length === 0 ? (
          <V4Row title={t(byProperty ? 'webAdmin.matches.emptyProperty' : 'webAdmin.matches.emptyDemand')} testId={`${testPrefix}-empty`} />
        ) : null}
        {items.map((m) => {
          const key = `${m.demandId}:${m.propertyId}`;
          const title = byProperty ? m.demand.title : (m.property.label ?? t('webAdmin.matches.unnamedProperty'));
          const statusPill =
            m.status === 'offered' ? (
              <V4Pill tone="accent">{t('webAdmin.matches.statusOffered')}</V4Pill>
            ) : m.status === 'dismissed' ? (
              <V4Pill>{t('webAdmin.matches.statusDismissed')}</V4Pill>
            ) : null;
          const score = (
            <V4Pill tone={m.score >= 80 ? 'accent' : 'neutral'} testId={`${testPrefix}-score-${key}`}>
              {t('webAdmin.matches.score', { score: m.score })}
            </V4Pill>
          );
          if (!byProperty) {
            return (
              <V4Row
                key={key}
                testId={`${testPrefix}-${key}`}
                title={title}
                sub={m.explanation}
                right={
                  <span className="rh-crm4-actions">
                    {statusPill}
                    {score}
                  </span>
                }
                chevron
                // Prostý odkaz, ne `<Link>` routeru: panel žije i v draweru
                // poptávky, který se kreslí (a testuje) bez kontextu routeru.
                render={(className, content) => (
                  <a href={`/nemovitosti/${m.propertyId}`} className={className} data-testid={`${testPrefix}-${key}`}>
                    {content}
                  </a>
                )}
              />
            );
          }
          return (
            <V4Row
              key={key}
              testId={`${testPrefix}-${key}`}
              title={
                <>
                  {title} {m.demand.contactName !== null ? <span className="rh-crm4-muted">· {m.demand.contactName}</span> : null}
                </>
              }
              sub={m.explanation}
              right={
                <span className="rh-crm4-actions">
                  {statusPill}
                  {score}
                  {m.status !== 'dismissed' ? (
                    <button
                      type="button"
                      className="rh-v4-btn rh-v4-btn--tinted"
                      disabled={busy || !m.demand.hasEmail}
                      title={m.demand.hasEmail ? undefined : t('webAdmin.matches.noEmail')}
                      onClick={() =>
                        void run(async () => {
                          setNote(null);
                          setPreview({ match: m, mail: await previewOffer(m.demandId, m.propertyId) });
                        })
                      }
                      data-testid={`${testPrefix}-offer-${key}`}
                    >
                      {t('webAdmin.matches.offer')}
                    </button>
                  ) : null}
                  {m.status === 'new' ? (
                    <button
                      type="button"
                      className="rh-v4-btn rh-v4-btn--plain"
                      disabled={busy}
                      onClick={() => void run(async () => { await dismissMatch(m.demandId, m.propertyId); await refresh(); })}
                      data-testid={`${testPrefix}-dismiss-${key}`}
                    >
                      {t('webAdmin.matches.dismiss')}
                    </button>
                  ) : null}
                </span>
              }
            />
          );
        })}
      </V4Group>

      {preview !== null ? (
        <V4Group testId={`${testPrefix}-preview`} label={t('webAdmin.matches.previewHeading')}>
          <div className="rh-v4-sect__body">
            <div className="rh-crm4-preview">
              <div className="rh-crm4-preview__meta">{t('webAdmin.viewings.previewTo', { to: preview.mail.to ?? '—' })}</div>
              <div className="rh-crm4-preview__subject" data-testid={`${testPrefix}-preview-subject`}>
                {preview.mail.subject}
              </div>
              <pre className="rh-crm4-preview__text">{preview.mail.text}</pre>
            </div>
            <p className="rh-a2-card__hint">{t('webAdmin.matches.previewHint')}</p>
            <div className="rh-crm4-actions">
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--filled"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await sendOffer(preview.match.demandId, preview.match.propertyId);
                    setPreview(null);
                    setNote(t('webAdmin.matches.offerQueued'));
                    await refresh();
                  })
                }
                data-testid={`${testPrefix}-send`}
              >
                <Send size={16} strokeWidth={2} aria-hidden="true" /> {t('webAdmin.matches.send')}
              </button>
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--plain"
                onClick={() => setPreview(null)}
                data-testid={`${testPrefix}-back`}
              >
                {t('webAdmin.matches.back')}
              </button>
            </div>
          </div>
        </V4Group>
      ) : null}
    </div>
  );
}
